import { FormProvider, useForm } from "react-hook-form"
import {
  DataDrivenFormSchema,
  Field,
  FormProperties,
  PageField,
  ValueRangeConfig,
} from "./schema"
import { zodResolver } from "@hookform/resolvers/zod"
import React from "react"
import {
  ArrayField,
  AttachmentField,
  ComboboxField,
  FieldRenderer,
  InputField,
  PlaceField,
  RadioField,
  RadioGroupField,
  RadioTableField,
  SliderField,
  SubjectField,
  TextAreaField,
  ToggleField,
  ValueRange,
} from "./fields"
import { TFunction } from "i18next"
import { FormRendererContext, useFormRenderer } from "./hooks"
import { AnchorHashSection, AnchorScrollingSection } from "./sections"
import { defineRegistry, getRenderer, Registry } from "./form-registry"
import { useTranslation } from "react-i18next"
import { SectionErrorProvider } from "./section-error-provider"
import { ErrorBanner } from "./errors"
import { DataDrivenAnchor } from "./anchor"
import { RHFDirtyDraftRegistration } from "@/components/shared/dirty-draft-navigation"

export const builtInRegistry = defineRegistry(
  AnchorHashSection,
  AnchorScrollingSection,
  InputField,
  ComboboxField,
  RadioTableField,
  ArrayField,
  TextAreaField,
  AttachmentField,
  RadioField,
  SliderField,
  PlaceField,
  ToggleField,
  SubjectField,
  ValueRange,
  RadioGroupField,
)

/**
 * The registry may be left to the built-ins only when the schema adds no
 * page-local field types; the schema alone decides which types those are.
 */
type RegistryProp<TExtra extends PageField> = [TExtra] extends [never]
  ? { registry?: Registry<NoInfer<TExtra>> }
  : { registry: Registry<NoInfer<TExtra>> }

export function FormRenderer<TExtra extends PageField = never>({
  schema,
  formId,
  onSubmit,
  defaultValues,
  titleId,
  formLabel,
  registerDirtyDraft = false,
  registry,
  scope = {},
}: {
  schema: DataDrivenFormSchema<any, TExtra>
  formId: string
  onSubmit: (data: any) => void
  defaultValues: any
  titleId?: string
  formLabel?: string
  registerDirtyDraft?: boolean
  scope?: Readonly<Record<string, unknown>>
} & RegistryProp<TExtra>) {
  const methods = useForm({
    resolver: zodResolver(schema.validator),
    defaultValues,
  })
  const { t } = useTranslation(schema.namespace)

  return (
    <FormRendererProvider tFunc={t} registry={registry} scope={scope}>
      <FormProvider {...methods}>
        {registerDirtyDraft && <RHFDirtyDraftRegistration />}
        <SectionErrorProvider>
          <ErrorBanner schema={schema} />
          <form
            role="form"
            id={formId}
            onSubmit={methods.handleSubmit(onSubmit)}
            className="relative flex min-h-0 flex-1 flex-col gap-5"
            aria-label={formLabel}
            aria-labelledby={titleId}
          >
            <DataDrivenAnchor schema={schema}>
              {schema.properties.map((property, index) => (
                <PropertyRenderer formProperty={property} key={index} />
              ))}
            </DataDrivenAnchor>
          </form>
        </SectionErrorProvider>
      </FormProvider>
    </FormRendererProvider>
  )
}

export function PropertyRenderer({
  formProperty,
}: {
  formProperty: FormProperties<any, PageField>
}) {
  const { registry } = useFormRenderer()
  if (
    formProperty.type.startsWith("field:") &&
    !formProperty.type.startsWith("field:custom:")
  ) {
    return (
      <FieldRenderer {...(formProperty as Exclude<Field, ValueRangeConfig>)} />
    )
  }

  const renderer = getRenderer(registry, formProperty)
  return renderer(formProperty)
}

export function FormRendererProvider({
  children,
  tFunc,
  registry = builtInRegistry,
  scope = {},
}: {
  children: React.ReactNode
  tFunc: TFunction
  registry?: Registry
  scope?: Readonly<Record<string, unknown>>
}) {
  return (
    <FormRendererContext.Provider value={{ tFunc, registry, scope }}>
      {children}
    </FormRendererContext.Provider>
  )
}
